import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { functionsApi } from "@/api/functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { DispatchMark } from "@/components/brand/dispatch-mark";
import { POLL } from "@/lib/poll";
import { Clock, Zap } from "lucide-react";

export const Route = createFileRoute("/_authed/functions/")({
  component: FunctionsPage,
});

function FunctionsPage() {
  const q = useQuery({
    queryKey: ["functions"],
    queryFn: () => functionsApi.list(),
    refetchInterval: POLL.config,
  });

  const fns = q.data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Build" title="Functions" />
      <Card className="overflow-hidden">
        {fns.length === 0 && !q.isLoading ? (
          <EmptyState
            icon={<DispatchMark className="h-8 w-auto" />}
            title="No functions synced"
            description="Register an App and sync its manifest to discover functions."
            action={
              <div className="flex items-center gap-3">
                <Button asChild size="sm" className="bg-brand text-primary-foreground hover:bg-brand/90">
                  <Link to="/apps">Add an app</Link>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <a href="/docs/quickstart/">Read the quickstart</a>
                </Button>
              </div>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Slug</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">App</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Trigger</TableHead>
                <TableHead className="pr-5 font-mono text-[11px] uppercase tracking-wider">Event / Cron</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fns.map((f) => (
                <TableRow key={f.id} className="group cursor-pointer">
                  <TableCell className="pl-5">
                    <Link to="/functions/$id" params={{ id: f.id }}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                      {f.slug}
                    </Link>
                  </TableCell>
                  <TableCell className="text-[13px] text-muted-foreground">{f.app_name}</TableCell>
                  <TableCell><TriggerBadge type={f.trigger_type} /></TableCell>
                  <TableCell className="pr-5 font-mono text-[12px] text-muted-foreground">
                    {f.trigger_event ?? f.trigger_cron ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export function TriggerBadge({ type }: { type: "event" | "cron" }) {
  const isCron = type === "cron";
  const Icon = isCron ? Clock : Zap;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${
      isCron ? "bg-pause/10 text-pause ring-pause/25" : "bg-run/10 text-run ring-run/25"
    }`}>
      <Icon size={11} /> {type}
    </span>
  );
}
